import React, { useState, useEffect } from "react";
import Sidebar from './Sidebar';
import MobileHeader from './MobileHeader';

const Layout = ({
  children,
  title = "Home",
  customHeader,
  chatHistory = [],
  chatsLoading = false,
  selectedChatId,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  userName,
  onLoadMoreChats,
  loadingMoreChats = false,
  hasMoreChats = false,
  newChatLoading = false,
  hideMobileHeader = false,
  onSidebarToggleRef,
  sidebarApiRef,
  initialSidebarCollapsed = false,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(initialSidebarCollapsed);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarCollapsed(true);
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(false);
        // Don't automatically change collapsed state on desktop - preserve user's choice
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setIsSidebarCollapsed(initialSidebarCollapsed);
  }, [initialSidebarCollapsed]);

  const handleToggleSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen((prev) => !prev);
    } else {
      setIsSidebarCollapsed((prev) => !prev);
    }
  };

  const handleExpandSidebar = () => {
    if (isMobile) setIsSidebarOpen(true);
    else setIsSidebarCollapsed(false);
  };

  const handleCloseSidebar = () => {
    if (isMobile) setIsSidebarOpen(false);
    else setIsSidebarCollapsed(true);
  };

  useEffect(() => {
    if (onSidebarToggleRef) onSidebarToggleRef.current = handleToggleSidebar;
  });

  useEffect(() => {
    if (!sidebarApiRef) return;
    sidebarApiRef.current = {
      toggle: handleToggleSidebar,
      collapse: handleCloseSidebar,
      expand: handleExpandSidebar,
      getState: () => ({
        isMobile,
        isSidebarCollapsed,
        isSidebarOpen,
      }),
    };
  }, [sidebarApiRef, isMobile, isSidebarCollapsed, isSidebarOpen]);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile overlay */}
      {isMobile && isSidebarOpen && (
        <div
          onClick={handleCloseSidebar}
          className="fixed inset-0 bg-black/50 z-[1200] transition-opacity"
        />
      )}

      <Sidebar
        isCollapsed={isSidebarCollapsed}
        isMobile={isMobile}
        isOpen={isSidebarOpen}
        onToggleCollapse={handleToggleSidebar}
        onExpand={handleExpandSidebar}
        onClose={handleCloseSidebar}
        chatHistory={chatHistory}
        chatsLoading={chatsLoading}
        selectedChatId={selectedChatId}
        onNewChat={onNewChat}
        onSelectChat={onSelectChat}
        onDeleteChat={onDeleteChat}
        userName={userName}
        onLoadMoreChats={onLoadMoreChats}
        loadingMoreChats={loadingMoreChats}
        hasMoreChats={hasMoreChats}
        newChatLoading={newChatLoading}
      />

      <div className="flex flex-col flex-1 overflow-hidden">
        {!hideMobileHeader && <MobileHeader title={title} onMenuClick={handleToggleSidebar} />}
        <main
          className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-white ${hideMobileHeader ? "" : "pb-[60px] md:pb-0"}`}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
